import { useCallback, useEffect, useMemo, useState } from 'react';
import classes from 'classnames';
import classNames from './MoveStudentsPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import StudentsTable, { convertStudentReportItemToStudentsTableData } from './StudentsTable';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import Button from '@/components/school-management/Button/Button';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { bulkService } from '@/school-management/services/bulk';
import { MoveClassDestination } from '@/school-management/types/services/bulk';
import AppLoaderCircle from '@/components/AppLoaderCircle';

interface ChooseDestinationProps {
  students: StudentReportItem[];
  onClose: () => void;
  onClassSelected: (selectedClassDestination: MoveClassDestination) => void;
}

const ChooseDestination = ({ students, onClose, onClassSelected }: ChooseDestinationProps) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [classList, setClassList] = useState<MoveClassDestination[]>([]);
  const [selectedClass, setSelectedClass] = useState<Option>();

  useEffect(() => {
    let isActive = true;

    const init = async () => {
      setIsLoading(true);
      const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
      let classList: MoveClassDestination[] = [];

      try {
        const r = await bulkService.getMoveClassDestinations(organizationId);
        classList = r?.data?.data?.classes || [];
      } catch {}

      if (!isActive) {
        return;
      }

      setClassList(classList);
      setIsLoading(false);
    };

    init();

    return () => {
      isActive = false;
    };
  }, [user]);

  const studentsTableData = useMemo(() => {
    return (students || []).map(s => convertStudentReportItemToStudentsTableData(s, t));
  }, [students, t]);

  const destinationsOptions = useMemo(() => {
    return classList.map(c => {
      return {
        label: `${c.groupName} | ${t(`grade_${c.gradeId}`)} ${t('grade')}`,
        value: c.groupId,
        data: c,
      } as Option;
    });
  }, [classList, t]);

  const optionRender = useCallback(
    (option: Option) => {
      return (
        <div
          className={classes(classNames.classListItem, {
            [classNames.classListItemDisable]: !option.data?.remainingCapacity,
          })}
        >
          <div className={classNames.classListItemLabel}>{option.label}</div>
          <div className={classNames.classListItemLeft}>
            {option.data?.remainingCapacity
              ? t('bulkActions.moveStudents.chooseDestination.spotsLeft', {
                  num: option.data.remainingCapacity,
                })
              : t('bulkActions.moveStudents.chooseDestination.full')}
          </div>
        </div>
      );
    },
    [t]
  );

  return (
    <>
      <div>
        <div className={classNames.title}>
          {t('bulkActions.moveStudents.chooseDestination.title')}
        </div>
        <div className={classNames.description}>
          {t('bulkActions.moveStudents.chooseDestination.description', { num: students.length })}
        </div>
      </div>
      <StudentsTable students={studentsTableData} />
      <div>
        <div className={classNames.subTitle}>
          {t('bulkActions.moveStudents.chooseDestination.subTitle')}
        </div>
        <div className={classNames.subDescription}>
          {t('bulkActions.moveStudents.chooseDestination.subDescription')}
        </div>
        <div className={classNames.classSelector}>
          <DropDown
            options={destinationsOptions}
            value={selectedClass}
            onChange={setSelectedClass}
            optionRender={optionRender}
            label={`${t('bulkActions.moveStudents.chooseDestination.selectClass') || ''}:`}
            listClassName={classNames.classList}
            autoClose={true}
          />
        </div>
      </div>
      <div className={classNames.footer}>
        <Button className={classNames.textButton} onClick={onClose}>
          {t('common.back')}
        </Button>
        <Button
          className={classNames.button}
          disabled={!selectedClass}
          onClick={() => {
            onClassSelected(selectedClass?.data as MoveClassDestination);
          }}
        >
          {t('bulkActions.moveStudents.chooseDestination.submitButton')}
        </Button>
      </div>
      {isLoading && (
        <div className={classNames.loader}>
          <AppLoaderCircle />
        </div>
      )}
    </>
  );
};

export default ChooseDestination;
